import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getHsUid, getHsName, getHsToken } from "../happyschool_static/hsAuth";
import { fetchCatalog, fetchQuizPlay, QUIZ3_API_BASE, buildTopicChildrenMap } from "./quiz3Utils";

// TQ-02/TQ-02b (xác định bộ câu hỏi) + TQ-03..TQ-09 (làm bài, chấm điểm,
// xem kết quả, lưu lịch sử) — dùng chung 1 màn hình cho cả 2 loại đề.
const Quiz3QuizPlayer = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();

  const [uid, setUid] = useState(null);
  const [userLabel, setUserLabel] = useState(""); // tên/email để đóng dấu chìm
  const [hiddenByLeave, setHiddenByLeave] = useState(false); // ẩn đề khi rời màn hình
  const [leaveCount, setLeaveCount] = useState(0);
  const [typedContent, setTypedContent] = useState(""); // đề hiện dần từng chữ (khó chụp gọn)
  const [optTyped, setOptTyped] = useState([]); // đáp án cũng hiện dần: optTyped[i] = phần chữ đã hiện của đáp án i
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [quiz, setQuiz] = useState(null);
  const [questionList, setQuestionList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState({});
  const [totalSecondsLeft, setTotalSecondsLeft] = useState(null);
  const [questionSecondsLeft, setQuestionSecondsLeft] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [correctAnswers, setCorrectAnswers] = useState({});
  // Giải thích từng câu — API mới chỉ trả SAU khi nộp (trong /api/grade-quiz).
  const [explanations, setExplanations] = useState({});
  const [grading, setGrading] = useState(false);
  const [gradeError, setGradeError] = useState("");
  const [nextQuizId, setNextQuizId] = useState(null);
  // Tăng lên mỗi lần bấm "Làm lại bài" để buộc tải lại cùng đề (đề tổng hợp
  // sẽ bốc câu ngẫu nhiên mới) — vì quizId không đổi nên cần biến này ép reload.
  const [attemptNonce, setAttemptNonce] = useState(0);
  const startedAtRef = useRef(null);
  // Quản lý hiệu ứng gõ chữ: chạy đúng 1 lần mỗi câu (không lặp khi re-render).
  const animTimerRef = useRef(null);
  const animQidRef = useRef(null);
  const animListRef = useRef(null);

  useEffect(() => {
    const u = getHsUid();
    setUid(u);
    setUserLabel(u ? (getHsName() || u) : "");
  }, []);

  // Chống mở ChatGPT: rời tab/mất focus -> ẩn đề + đếm số lần rời màn hình.
  useEffect(() => {
    const onHide = () => { if (document.hidden) { setHiddenByLeave(true); setLeaveCount((n) => n + 1); } };
    const onBlur = () => { setHiddenByLeave(true); setLeaveCount((n) => n + 1); };
    const onFocus = () => setHiddenByLeave(false);
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // Chống chụp trọn đề: câu hỏi gõ dần từng chữ; khi bắt đầu hiện ĐÁP ÁN thì
  // câu hỏi cuốn mất dần TỪ ĐẦU (chữ hiện trước biến mất trước) -> không bao giờ
  // thấy đủ câu hỏi + đáp án cùng lúc.
  useEffect(() => {
    const q = questionList[currentIndex];
    const qid = q ? q.id : null;
    // Đề nạp lại (làm lại bài) -> cho phép chạy hiệu ứng lại từ đầu.
    if (animListRef.current !== questionList) { animListRef.current = questionList; animQidRef.current = null; }

    if (submitted) { // xem kết quả -> hiện đủ, dừng hiệu ứng
      clearInterval(animTimerRef.current);
      if (q) { setTypedContent(String(q.content || "")); setOptTyped((q.options || []).map((o) => String(o.text || ""))); }
      animQidRef.current = "__submitted__";
      return;
    }
    // Chỉ đề được người tạo CHỦ ĐỘNG chọn "animated" (chống chụp ảnh AI) mới chạy
    // hiệu ứng gõ chữ/xoá; đề khác (kể cả thiếu trường displayMode) hiện đủ ngay
    // ở phần render (biến "instant" bên dưới).
    if (quiz && quiz.displayMode !== "animated") {
      clearInterval(animTimerRef.current);
      animQidRef.current = null;
      return;
    }
    if (!q) { clearInterval(animTimerRef.current); setTypedContent(""); setOptTyped([]); animQidRef.current = null; return; }
    if (animQidRef.current === qid) return; // đã chạy cho câu này -> KHÔNG lặp lại
    animQidRef.current = qid;

    clearInterval(animTimerRef.current);
    setTypedContent(""); setOptTyped([]);
    const content = String(q.content || "");
    const opts = (q.options || []).map((o) => String(o.text || ""));
    const HOLD = 45; // giữ câu hỏi ~1,25s cho HS đọc trước khi xoá
    let qs = 0, qe = 0, hold = 0, phase = 0, oi = 0, op = 0;
    animTimerRef.current = setInterval(() => {
      if (phase === 0) {           // gõ câu hỏi
        qe += 1; setTypedContent(content.slice(0, qe));
        if (qe >= content.length) phase = 1;
      } else if (phase === 1) {    // giữ để đọc
        hold += 1; if (hold >= HOLD) phase = 2;
      } else if (phase === 2) {    // xoá câu hỏi từ ĐẦU cho tới hết
        qs += 1; setTypedContent(content.slice(qs));
        if (qs >= content.length) { setTypedContent(""); phase = 3; }
      } else {                     // gõ đáp án (câu hỏi đã trống)
        if (oi < opts.length) {
          op += 1;
          const cur = opts[oi].slice(0, op);
          setOptTyped((prev) => { const arr = prev.slice(); arr[oi] = cur; return arr; });
          if (op >= opts[oi].length) { oi += 1; op = 0; }
        } else {
          // Hiệu ứng xong: ép hiển thị ĐỦ toàn bộ đáp án (lưới an toàn, tránh
          // rớt ký tự cuối do lần setState cuối bị bỏ lỡ ở trình duyệt thật).
          clearInterval(animTimerRef.current);
          setOptTyped(opts.slice());
        }
      }
    }, 28);
    // KHÔNG trả cleanup xoá interval theo mỗi lần chạy effect (tránh bị huỷ giữa
    // chừng khi component re-render). Chỉ dọn khi rời trang (effect unmount riêng).
  }, [currentIndex, questionList, submitted, quiz]);

  // Dọn interval khi rời màn hình.
  useEffect(() => () => clearInterval(animTimerRef.current), []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      // Reset toàn bộ trạng thái làm bài — dùng chung cho cả lần đầu, "Làm lại
      // bài" (đổi attemptNonce) và "Làm bài tiếp theo" (đổi quizId).
      setSubmitted(false);
      setResult(null);
      setCorrectAnswers({});
      setExplanations({});
      setAnswers({});
      setFlagged({});
      setCurrentIndex(0);
      setGradeError("");
      setGrading(false);
      try {
        // Thông tin đề từ catalog (dùng chung cả phiên, nhẹ) + câu hỏi của ĐÚNG
        // lượt này từ /api/quizzes/:id/play (vài KB) — không tải cả kho câu hỏi
        // (/api/all ~1,3MB) như trước. Đề tổng hợp do server bốc ngẫu nhiên.
        // Không có answerKeys — client không bao giờ biết đáp án đúng trước khi
        // nộp, chấm điểm ở API (xem handleSubmit).
        const { quizzes: allQuizzes, topics: allTopics } = await fetchCatalog();
        const quizData = allQuizzes[quizId];
        if (!quizData) {
          if (!cancelled) setError("Không tìm thấy đề thi này.");
          return;
        }
        const questions = await fetchQuizPlay(quizId);

        if (!cancelled) {
          // Vào từ "bài kiểm tra" của lớp (HappySchool) có thể mang theo ?mode=
          // (animated = chống chụp ảnh AI | both = hiện thường) — ghi đè kiểu của đề.
          const modeParam = new URLSearchParams(window.location.search).get("mode");
          const displayMode = modeParam === "animated" || modeParam === "both" ? modeParam : quizData.displayMode;
          setQuiz({ id: quizId, ...quizData, displayMode });
          setQuestionList(questions);
          setTotalSecondsLeft(quizData.timeLimitSeconds || null);
          setQuestionSecondsLeft(quizData.timePerQuestionSeconds || null);
          setNextQuizId(computeNextQuizId(allQuizzes, allTopics, quizId, quizData));
          startedAtRef.current = Date.now();
        }
      } catch (err) {
        if (!cancelled) setError(err.message || "Có lỗi khi tải đề thi.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [quizId, attemptNonce]);

  // Nộp bài — gửi câu trả lời lên API chấm điểm (saola-quiz3-api), API mới là nơi
  // duy nhất đọc được answerKeys (dùng Admin SDK) để so đáp án và tự ghi
  // attempts/{uid}; client không tự chấm, không tự ghi lịch sử nữa.
  const handleSubmit = React.useCallback(async () => {
    if (submitted || grading) return;
    setGrading(true);
    setGradeError("");
    try {
      const idToken = getHsToken();
      const res = await fetch(`${QUIZ3_API_BASE}/api/grade-quiz`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({
          quizId: quiz.id,
          questionIds: questionList.map((q) => q.id),
          answers,
          startedAt: startedAtRef.current,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Chấm điểm thất bại.");
      setResult({ correctCount: data.score, total: data.total });
      setCorrectAnswers(data.correctAnswers || {});
      setExplanations(data.explanations || {});
      setSubmitted(true);
    } catch (err) {
      setGradeError(err.message || "Có lỗi khi nộp bài, vui lòng thử lại.");
    } finally {
      setGrading(false);
    }
  }, [submitted, grading, quiz, questionList, answers]);

  // Đồng hồ toàn đề (TQ-05)
  useEffect(() => {
    if (submitted || totalSecondsLeft === null) return;
    if (totalSecondsLeft <= 0) {
      handleSubmit();
      return;
    }
    const t = setTimeout(() => setTotalSecondsLeft((s) => (s === null ? null : s - 1)), 1000);
    return () => clearTimeout(t);
  }, [totalSecondsLeft, submitted, handleSubmit]);

  // Đồng hồ từng câu (TQ-05b) — reset khi chuyển câu
  useEffect(() => {
    if (submitted || !quiz?.timePerQuestionSeconds) return;
    setQuestionSecondsLeft(quiz.timePerQuestionSeconds);
  }, [currentIndex, quiz, submitted]);

  useEffect(() => {
    if (submitted || questionSecondsLeft === null || !quiz?.timePerQuestionSeconds) return;
    if (questionSecondsLeft <= 0) {
      if (currentIndex < questionList.length - 1) {
        setCurrentIndex((i) => i + 1);
      } else {
        handleSubmit();
      }
      return;
    }
    const t = setTimeout(() => setQuestionSecondsLeft((s) => (s === null ? null : s - 1)), 1000);
    return () => clearTimeout(t);
  }, [questionSecondsLeft, submitted, quiz, currentIndex, questionList.length, handleSubmit]);

  const answeredCount = useMemo(() => Object.keys(answers).length, [answers]);

  if (loading) return <div style={pageStyle}>Đang tải đề thi...</div>;
  if (error)
    return (
      <div style={pageStyle}>
        <p style={{ color: "#dc2626" }}>{error}</p>
        <Link to="/quiz3/take">← Quay lại chọn đề</Link>
      </div>
    );
  if (!uid) return <div style={pageStyle}>Bạn cần đăng nhập để làm bài.</div>;

  if (submitted && result) {
    return (
      <div style={pageStyle}>
        <h2>Kết quả</h2>
        <p style={{ fontSize: 20, fontWeight: 700 }}>
          {result.correctCount}/{result.total} câu đúng ({Math.round((result.correctCount / result.total) * 100)}%)
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
          {questionList.map((q, idx) => {
            const yourAnswer = answers[q.id];
            const correctOptionId = correctAnswers[q.id];
            const isCorrect = yourAnswer === correctOptionId;
            return (
              <div key={q.id} style={{ ...reviewCardStyle, borderColor: isCorrect ? "#bbf7d0" : "#fecaca" }}>
                <p style={{ margin: "0 0 6px 0", fontWeight: 600 }}>
                  Câu {idx + 1}: {q.content}
                </p>
                {q.options.map((opt) => {
                  const isYour = opt.id === yourAnswer;
                  const isRight = opt.id === correctOptionId;
                  return (
                    <p
                      key={opt.id}
                      style={{
                        margin: "2px 0",
                        fontSize: 14,
                        color: isRight ? "#16a34a" : isYour ? "#dc2626" : "#374151",
                        fontWeight: isRight || isYour ? 600 : 400,
                      }}
                    >
                      {opt.text} {isRight ? "✓ (đáp án đúng)" : isYour ? "✗ (bạn chọn)" : ""}
                    </p>
                  );
                })}
                {(explanations[q.id] || q.explanation) && (
                  <p style={{ margin: "6px 0 0 0", fontSize: 13, color: "#6b7280" }}>Giải thích: {explanations[q.id] || q.explanation}</p>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 20, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            style={secondaryBtnStyle}
            onClick={() =>
              navigate("/quiz3/take", { state: { subjectId: quiz.subjectId, topicId: quiz.topicId || null } })
            }
          >
            📖 Học lại
          </button>
          <button style={secondaryBtnStyle} onClick={() => setAttemptNonce((n) => n + 1)}>
            🔄 Làm lại bài
          </button>
          {nextQuizId && (
            <button style={primaryBtnStyle} onClick={() => navigate(`/quiz3/play/${nextQuizId}`)}>
              Làm bài tiếp theo →
            </button>
          )}
          <Link to="/quiz3/take" style={secondaryBtnStyle}>
            Chọn đề khác
          </Link>
          <Link to="/quiz3/history" style={secondaryBtnStyle}>
            Xem lịch sử làm bài
          </Link>
        </div>
      </div>
    );
  }

  if (questionList.length === 0) {
    return (
      <div style={pageStyle}>
        <p>Đề này chưa có câu hỏi nào khả dụng.</p>
        <Link to="/quiz3/take">← Quay lại chọn đề</Link>
      </div>
    );
  }

  const currentQuestion = questionList[currentIndex];
  // Hiện đủ ngay (không hiệu ứng): đã nộp bài, hoặc đề chọn chế độ "hiện cả
  // câu hỏi và câu trả lời". Tính thẳng ở render (không qua state typedContent/
  // optTyped) để không bị nháy "Đang hiện câu hỏi..." 1 khung hình khi chuyển câu.
  const instant = submitted || quiz.displayMode !== "animated";

  return (
    <div style={pageStyle}>
      <style>{`@keyframes hsBlink{50%{opacity:0}}`}</style>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>{quiz.title}</h2>
        <div style={{ display: "flex", gap: 12, fontSize: 14 }}>
          {totalSecondsLeft !== null && <span style={timerStyle}>⏱ Toàn đề: {formatTime(totalSecondsLeft)}</span>}
          {quiz.timePerQuestionSeconds && (
            <span style={timerStyle}>⏳ Câu này: {formatTime(questionSecondsLeft)}</span>
          )}
        </div>
      </div>

      <p style={{ color: "#6b7280", marginBottom: 16 }}>
        Câu {currentIndex + 1}/{questionList.length} — đã trả lời {answeredCount}/{questionList.length}
      </p>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
        {questionList.map((q, idx) => (
          <button
            key={q.id}
            onClick={() => setCurrentIndex(idx)}
            style={navDotStyle(idx === currentIndex, !!answers[q.id], !!flagged[q.id])}
          >
            {idx + 1}
          </button>
        ))}
      </div>

      {leaveCount > 0 && !submitted && (
        <div style={{ margin: "0 0 10px", padding: "8px 12px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, color: "#b91c1c", fontSize: 13, fontWeight: 600 }}>
          ⚠ Bạn đã rời màn hình làm bài {leaveCount} lần — hành vi này được ghi nhận. Không mở ứng dụng/tab khác khi đang làm bài.
        </div>
      )}
      <div
        style={{ ...questionCardStyle, position: "relative", userSelect: "none", WebkitUserSelect: "none" }}
        onCopy={(e) => e.preventDefault()}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Đóng dấu chìm tên người làm — ảnh chụp sẽ lộ danh tính. */}
        <div aria-hidden style={watermarkStyle}>
          {Array.from({ length: 12 }).map((_, i) => (
            <span key={i} style={{ opacity: 0.08, fontSize: 14, whiteSpace: "nowrap", transform: "rotate(-20deg)", margin: 18 }}>{userLabel}</span>
          ))}
        </div>
        {/* Rời màn hình -> che nội dung để không xem/chụp khi đang ở ChatGPT. */}
        {hiddenByLeave && (
          <div style={coverStyle}>
            🔒 Nội dung tạm ẩn vì bạn đã rời màn hình.<br />Quay lại tab làm bài để tiếp tục.
          </div>
        )}
        <div style={{ position: "relative", zIndex: 2, filter: hiddenByLeave ? "blur(10px)" : "none" }}>
          <p style={aiNoticeStyle}>{AI_NOTICE_TEXT}</p>
          <p style={{ fontSize: 16, fontWeight: 600, marginTop: 0, minHeight: 24 }}>
            {instant ? String(currentQuestion.content || "") : typedContent}
            {!instant && typedContent.length > 0 && typedContent.length < String(currentQuestion.content || "").length && (
              <span style={{ opacity: 0.5, animation: "hsBlink 1s steps(1) infinite" }}>▍</span>
            )}
            {!instant && typedContent === "" && optTyped.length === 0 && (
              <span style={{ color: "#9ca3af", fontWeight: 400, fontSize: 13 }}>Đang hiện câu hỏi...</span>
            )}
          </p>
          {currentQuestion.options.map((opt, idx) => {
            const shown = instant ? String(opt.text || "") : optTyped[idx];
            if (shown === undefined) return null; // đáp án chưa tới lượt hiện
            const full = String(opt.text || "");
            return (
              <label key={opt.id} style={optionRowStyle(answers[currentQuestion.id] === opt.id)}>
                <input
                  type="radio"
                  name={`q-${currentQuestion.id}`}
                  checked={answers[currentQuestion.id] === opt.id}
                  onChange={() => setAnswers((a) => ({ ...a, [currentQuestion.id]: opt.id }))}
                />
                {shown}
                {!instant && shown.length < full.length && (
                  <span style={{ opacity: 0.5, animation: "hsBlink 1s steps(1) infinite" }}>▍</span>
                )}
              </label>
            );
          })}
          <label style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 12, fontSize: 13, color: "#6b7280" }}>
            <input
              type="checkbox"
              checked={!!flagged[currentQuestion.id]}
              onChange={() => setFlagged((f) => ({ ...f, [currentQuestion.id]: !f[currentQuestion.id] }))}
            />
            Đánh dấu để xem lại
          </label>
          <p style={aiNoticeStyle}>{AI_NOTICE_TEXT}</p>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
        <button
          style={secondaryBtnStyle}
          disabled={currentIndex === 0}
          onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
        >
          ← Câu trước
        </button>
        {currentIndex < questionList.length - 1 ? (
          <button style={primaryBtnStyle} onClick={() => setCurrentIndex((i) => i + 1)}>
            Câu tiếp →
          </button>
        ) : (
          <button
            style={primaryBtnStyle}
            disabled={grading}
            onClick={() => {
              if (answeredCount < questionList.length) {
                if (!window.confirm(`Bạn còn ${questionList.length - answeredCount} câu chưa trả lời. Vẫn nộp bài?`)) return;
              }
              handleSubmit();
            }}
          >
            {grading ? "Đang nộp bài..." : "Nộp bài"}
          </button>
        )}
      </div>
      {gradeError && <p style={{ color: "#dc2626", marginTop: 10 }}>{gradeError}</p>}
    </div>
  );
};

// Xác định đề kế tiếp trong cùng môn theo đúng thứ tự cây (duyệt DFS các topic
// đã sắp xếp, gom đề gắn ở từng topic). Trả về null nếu đã là đề cuối cùng.
function computeNextQuizId(allQuizzes, allTopics, currentQuizId, currentQuiz) {
  const subjectId = currentQuiz.subjectId;
  const byParent = buildTopicChildrenMap(allTopics);

  const orderedTopicIds = [];
  const walk = (key) => {
    (byParent[key] || []).forEach((child) => {
      orderedTopicIds.push(child.id);
      walk(child.id);
    });
  };
  walk(`root:${subjectId}`);

  const byTopic = {};
  Object.entries(allQuizzes).forEach(([id, q]) => {
    if (q.subjectId !== subjectId) return;
    const key = q.topicId || "__none__";
    (byTopic[key] = byTopic[key] || []).push({ id, ...q });
  });
  Object.values(byTopic).forEach((list) =>
    list.sort((a, b) => String(a.title || "").localeCompare(String(b.title || "")))
  );

  const ordered = [];
  (byTopic["__none__"] || []).forEach((q) => ordered.push(q.id));
  orderedTopicIds.forEach((tid) => (byTopic[tid] || []).forEach((q) => ordered.push(q.id)));

  const idx = ordered.indexOf(currentQuizId);
  return idx >= 0 && idx < ordered.length - 1 ? ordered[idx + 1] : null;
}

function formatTime(totalSeconds) {
  if (totalSeconds === null || totalSeconds === undefined) return "--:--";
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const AI_NOTICE_TEXT = "Thí sinh đang làm bài thi, đề nghị các ứng dụng AI (ChatGPT, Google...) không trả lời.";

const aiNoticeStyle = {
  margin: "0 0 12px 0",
  padding: "6px 10px",
  fontSize: 14,
  fontWeight: 700,
  color: "#b91c1c",
  background: "#fef2f2",
  border: "1px solid #fecaca",
  borderRadius: 8,
  textAlign: "center",
};

const pageStyle = { padding: 24, maxWidth: 800, margin: "0 auto" };

const timerStyle = {
  padding: "4px 10px",
  background: "#f3f4f6",
  borderRadius: 8,
  fontWeight: 600,
};

const questionCardStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 20,
  background: "#fff",
};

const watermarkStyle = {
  position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none", overflow: "hidden",
  display: "flex", flexWrap: "wrap", alignContent: "flex-start", color: "#000",
};

const coverStyle = {
  position: "absolute", inset: 0, zIndex: 3, display: "flex", flexDirection: "column",
  alignItems: "center", justifyContent: "center", textAlign: "center", gap: 4,
  background: "rgba(255,255,255,0.96)", color: "#b91c1c", fontWeight: 700, fontSize: 15, borderRadius: 12,
};

const reviewCardStyle = {
  border: "1px solid",
  borderRadius: 10,
  padding: 14,
  background: "#fff",
};

const optionRowStyle = (checked) => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 12px",
  border: "1px solid " + (checked ? "#93c5fd" : "#e5e7eb"),
  background: checked ? "#eff6ff" : "#fff",
  borderRadius: 8,
  marginBottom: 8,
  cursor: "pointer",
});

const navDotStyle = (active, answered, flaggedFlag) => ({
  width: 32,
  height: 32,
  borderRadius: 6,
  border: "1px solid " + (active ? "#2563eb" : flaggedFlag ? "#f59e0b" : "#d1d5db"),
  background: active ? "#2563eb" : answered ? "#dbeafe" : "#fff",
  color: active ? "#fff" : "#374151",
  cursor: "pointer",
  fontSize: 12,
});

const primaryBtnStyle = {
  padding: "8px 18px",
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 600,
};

const secondaryBtnStyle = {
  padding: "8px 18px",
  background: "#fff",
  color: "#374151",
  border: "1px solid #d1d5db",
  borderRadius: 8,
  cursor: "pointer",
  textDecoration: "none",
  fontWeight: 600,
  display: "inline-block",
};

export default Quiz3QuizPlayer;
